import type {
  ComposePdfViewportRotation,
  CreateDisplayTransform,
  DisplayBbox,
  DisplayPoint,
  GetRasterRotationOffset,
  GetRotatedSize,
  PageRotation,
} from "./types";

export const PAGE_ROTATIONS: readonly PageRotation[] = [0, 90, 180, 270];

export const isPageRotation = (value: unknown): value is PageRotation =>
  typeof value === "number" &&
  PAGE_ROTATIONS.some((rotation) => rotation === value);

export const nextPageRotation = (rotation: PageRotation): PageRotation => {
  const index = PAGE_ROTATIONS.indexOf(rotation);
  return PAGE_ROTATIONS[(index + 1) % PAGE_ROTATIONS.length];
};

// pdf.js replaces the page's own /Rotate when given a rotation, so compose on top of it.
export const composePdfViewportRotation: ComposePdfViewportRotation = ({
  pageRotate,
  rotation,
}) => (pageRotate + rotation) % 360;

const isQuarterTurn = (rotation: PageRotation): boolean =>
  rotation === 90 || rotation === 270;

export const getRotatedSize: GetRotatedSize = ({ rotation, width, height }) =>
  isQuarterTurn(rotation)
    ? { width: height, height: width }
    : { width, height };

// Offset that brings a raster rotated clockwise about its top-left corner back into the positive quadrant.
export const getRasterRotationOffset: GetRasterRotationOffset = ({
  rotation,
  width,
  height,
}) => {
  switch (rotation) {
    case 90: {
      return { translateX: height, translateY: 0 };
    }
    case 180: {
      return { translateX: width, translateY: height };
    }
    case 270: {
      return { translateX: 0, translateY: width };
    }
    default: {
      return { translateX: 0, translateY: 0 };
    }
  }
};

const rotatePoint = (
  point: DisplayPoint,
  rotation: PageRotation,
  width: number,
  height: number,
): DisplayPoint => {
  switch (rotation) {
    case 90: {
      return { x: height - point.y, y: point.x };
    }
    case 180: {
      return { x: width - point.x, y: height - point.y };
    }
    case 270: {
      return { x: point.y, y: width - point.x };
    }
    default: {
      return point;
    }
  }
};

const unrotatePoint = (
  point: DisplayPoint,
  rotation: PageRotation,
  width: number,
  height: number,
): DisplayPoint => {
  switch (rotation) {
    case 90: {
      return { x: point.y, y: height - point.x };
    }
    case 180: {
      return { x: width - point.x, y: height - point.y };
    }
    case 270: {
      return { x: width - point.y, y: point.x };
    }
    default: {
      return point;
    }
  }
};

export const createDisplayTransform: CreateDisplayTransform = ({
  rotation,
  scaleFactor,
  unrotatedDisplayWidth,
  unrotatedDisplayHeight,
}) => {
  const isIdentity = rotation === 0 && scaleFactor === 1;

  const toDisplay = (point: DisplayPoint): DisplayPoint => {
    if (isIdentity) return point;
    const scaled = { x: point.x * scaleFactor, y: point.y * scaleFactor };
    return rotatePoint(
      scaled,
      rotation,
      unrotatedDisplayWidth,
      unrotatedDisplayHeight,
    );
  };

  const fromDisplay = (point: DisplayPoint): DisplayPoint => {
    if (isIdentity) return point;
    const unrotated = unrotatePoint(
      point,
      rotation,
      unrotatedDisplayWidth,
      unrotatedDisplayHeight,
    );
    return { x: unrotated.x / scaleFactor, y: unrotated.y / scaleFactor };
  };

  const mapBbox = (
    bbox: DisplayBbox,
    mapPoint: (point: DisplayPoint) => DisplayPoint,
  ): DisplayBbox => {
    if (isIdentity) return bbox;
    const first = mapPoint({ x: bbox.x1, y: bbox.y1 });
    const second = mapPoint({ x: bbox.x2, y: bbox.y2 });
    return {
      x1: Math.min(first.x, second.x),
      y1: Math.min(first.y, second.y),
      x2: Math.max(first.x, second.x),
      y2: Math.max(first.y, second.y),
    };
  };

  return {
    toDisplay,
    fromDisplay,
    toDisplayBbox: (bbox) => mapBbox(bbox, toDisplay),
    fromDisplayBbox: (bbox) => mapBbox(bbox, fromDisplay),
    toDisplayRect: (rect) => {
      if (isIdentity) return rect;
      const first = toDisplay({ x: rect.minX, y: rect.minY });
      const second = toDisplay({ x: rect.maxX, y: rect.maxY });
      return {
        minX: Math.min(first.x, second.x),
        minY: Math.min(first.y, second.y),
        maxX: Math.max(first.x, second.x),
        maxY: Math.max(first.y, second.y),
      };
    },
    isIdentity,
  };
};
